import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";
import { FloatingCreateButton } from "./components/FloatingCreateButton";
import { OfflineBanner } from "./components/OfflineBanner";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { OnlinePresenceProvider } from "./contexts/OnlinePresenceContext";
import { AuthProvider } from "./contexts/AuthContext";
import { useRealtimeNotifications } from "./hooks/useRealtimeNotifications";
import { useMatchStatusUpdater } from "./hooks/useMatchStatusUpdater";
import { Loader2 } from "lucide-react";

// Eager imports — critical path
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";

// Lazy imports — loaded on demand
const Clubs = lazy(() => import("./pages/Clubs"));
const ClubDetail = lazy(() => import("./pages/ClubDetail"));
const ClubManage = lazy(() => import("./pages/ClubManage"));
const ClubChallenges = lazy(() => import("./pages/ClubChallenges"));
const ClubInvitations = lazy(() => import("./pages/ClubInvitations"));
const ClubLeaderboard = lazy(() => import("./pages/ClubLeaderboard"));
const Matches = lazy(() => import("./pages/Matches"));
const CreateMatch = lazy(() => import("./pages/CreateMatch"));
const MatchManage = lazy(() => import("./pages/MatchManage"));
const MatchDetail = lazy(() => import("./pages/MatchDetail"));
const MatchResultConfirm = lazy(() => import("./pages/MatchResultConfirm"));
const Profile = lazy(() => import("./pages/Profile"));
const Following = lazy(() => import("./pages/Following"));
const FollowingActivity = lazy(() => import("./pages/FollowingActivity"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Chat = lazy(() => import("./pages/Chat"));
const ChatDetail = lazy(() => import("./pages/ChatDetail"));
const Players = lazy(() => import("./pages/Players"));
const PlayerDetail = lazy(() => import("./pages/PlayerDetail"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const CourtsNew = lazy(() => import("./pages/CourtsNew"));
const CourtDetail = lazy(() => import("./pages/CourtDetail"));
const SubCourtDetail = lazy(() => import("./pages/SubCourtDetail"));
const CourtSlotDetail = lazy(() => import("./pages/CourtSlotDetail"));
const CreateCourtSchedule = lazy(() => import("./pages/CreateCourtSchedule"));
const CreateCourtSlot = lazy(() => import("./pages/CreateCourtSlot"));
const MyCourts = lazy(() => import("./pages/MyCourts"));
const MyRequests = lazy(() => import("./pages/MyRequests"));
const BookingRequestDetail = lazy(() => import("./pages/requests/BookingRequestDetail"));
const MatchJoinRequestDetail = lazy(() => import("./pages/requests/MatchJoinRequestDetail"));
const ClassEnrollmentDetail = lazy(() => import("./pages/requests/ClassEnrollmentDetail"));
const SpectatorRequestDetail = lazy(() => import("./pages/requests/SpectatorRequestDetail"));
const MyTrainingSchedule = lazy(() => import("./pages/MyTrainingSchedule"));
const FavoriteCourts = lazy(() => import("./pages/FavoriteCourts"));
const CourtBookingManagement = lazy(() => import("./pages/CourtBookingManagement"));
const CourtBookingHistory = lazy(() => import("./pages/CourtBookingHistory"));
const CourtScheduleManagement = lazy(() => import("./pages/CourtScheduleManagement"));
const CourtServices = lazy(() => import("./pages/CourtServices"));
const Classes = lazy(() => import("./pages/Classes"));
const ClassDetail = lazy(() => import("./pages/ClassDetail"));
const MyClasses = lazy(() => import("./pages/MyClasses"));
const LearningHistory = lazy(() => import("./pages/LearningHistory"));
const MyTeams = lazy(() => import("./pages/MyTeams"));
const TeamDetail = lazy(() => import("./pages/TeamDetail"));
const TeamInvitations = lazy(() => import("./pages/TeamInvitations"));
const TeamMatchInvitations = lazy(() => import("./pages/TeamMatchInvitations"));
const InvitationManagement = lazy(() => import("./pages/InvitationManagement"));
const Settings = lazy(() => import("./pages/Settings"));
const PrivacySettings = lazy(() => import("./pages/PrivacySettings"));
const ProviderEarnings = lazy(() => import("./pages/ProviderEarnings"));
const PaymentDetail = lazy(() => import("./pages/PaymentDetail"));
const PaymentHistory = lazy(() => import("./pages/PaymentHistory"));
const Install = lazy(() => import("./pages/Install"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminDisputes = lazy(() => import("./pages/AdminDisputes"));
const AdminCourtComplaints = lazy(() => import("./pages/AdminCourtComplaints"));
const AdminBannedCourts = lazy(() => import("./pages/AdminBannedCourts"));
const AdminCourtVerification = lazy(() => import("./pages/AdminCourtVerification"));
const AdminPayments = lazy(() => import("./pages/AdminPayments"));
const AdminPlayerBans = lazy(() => import("./pages/AdminPlayerBans"));
const AdminEscrow = lazy(() => import("./pages/AdminEscrow"));
const AdminPaymentReconcile = lazy(() => import("./pages/AdminPaymentReconcile"));
const AdminPayouts = lazy(() => import("./pages/AdminPayouts"));
const AdminCoachVerification = lazy(() => import("./pages/AdminCoachVerification"));
const AdminUserManagement = lazy(() => import("./pages/AdminUserManagement"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminReports = lazy(() => import("./pages/AdminReports"));
const AdminFeeConfig = lazy(() => import("./pages/AdminFeeConfig"));
const AdminAITester = lazy(() => import("./pages/AdminAITester"));
const AdminAITesterHistory = lazy(() => import("./pages/AdminAITesterHistory"));
const AdminCronLogs = lazy(() => import("./pages/AdminCronLogs"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      networkMode: 'online',
    },
    mutations: {
      networkMode: 'online',
    },
  },
});

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function RealtimeNotificationsProvider({ children }: { children: React.ReactNode }) {
  useRealtimeNotifications();
  useMatchStatusUpdater();
  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <RealtimeNotificationsProvider>
              <OnlinePresenceProvider>
                <OfflineBanner />
                <UpdatePrompt />
                <FloatingCreateButton />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/clubs" element={<Clubs />} />
                    <Route path="/clubs/:id" element={<ClubDetail />} />
                    <Route path="/matches" element={<Matches />} />
                    <Route path="/matches/:id" element={<MatchDetail />} />
                    <Route path="/players" element={<Players />} />
                    <Route path="/players/:id" element={<PlayerDetail />} />
                    <Route path="/leaderboard" element={<Leaderboard />} />
                    <Route path="/clubs/leaderboard" element={<ClubLeaderboard />} />
                    <Route path="/courts" element={<CourtsNew />} />
                    <Route path="/courts/detail/:id" element={<CourtDetail />} />
                    <Route path="/courts/detail/:id/sub/:subId" element={<SubCourtDetail />} />
                    <Route path="/courts/:id" element={<CourtSlotDetail />} />
                    <Route path="/classes" element={<Classes />} />
                    <Route path="/classes/:id" element={<ClassDetail />} />
                    <Route path="/install" element={<Install />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                    {/* Protected routes */}
                    <Route path="/clubs/:id/manage" element={<ProtectedRoute><ClubManage /></ProtectedRoute>} />
                    <Route path="/clubs/:id/challenges" element={<ProtectedRoute><ClubChallenges /></ProtectedRoute>} />
                    <Route path="/matches/create" element={<ProtectedRoute><CreateMatch /></ProtectedRoute>} />
                    <Route path="/matches/manage" element={<ProtectedRoute><MatchManage /></ProtectedRoute>} />
                    <Route path="/matches/:id/manage" element={<ProtectedRoute><MatchManage /></ProtectedRoute>} />
                    <Route path="/matches/:id/confirm-result" element={<ProtectedRoute><MatchResultConfirm /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/profile/following" element={<ProtectedRoute><Following /></ProtectedRoute>} />
                    <Route path="/following" element={<ProtectedRoute><FollowingActivity /></ProtectedRoute>} />
                    <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                    <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
                    <Route path="/chat/:id" element={<ProtectedRoute><ChatDetail /></ProtectedRoute>} />
                    <Route path="/earnings" element={<ProtectedRoute><ProviderEarnings /></ProtectedRoute>} />
                    <Route path="/payment-history" element={<ProtectedRoute><PaymentHistory /></ProtectedRoute>} />
                    <Route path="/payments/:id" element={<ProtectedRoute><PaymentDetail /></ProtectedRoute>} />
                    <Route path="/courts/create" element={<ProtectedRoute><CreateCourtSchedule /></ProtectedRoute>} />
                    <Route path="/courts/create-slot" element={<ProtectedRoute><CreateCourtSlot /></ProtectedRoute>} />
                    <Route path="/courts/my-courts" element={<ProtectedRoute><MyCourts /></ProtectedRoute>} />
                    <Route path="/courts/favorites" element={<ProtectedRoute><FavoriteCourts /></ProtectedRoute>} />
                    <Route path="/my-requests" element={<ProtectedRoute><MyRequests /></ProtectedRoute>} />
                    <Route path="/my-requests/booking/:id" element={<ProtectedRoute><BookingRequestDetail /></ProtectedRoute>} />
                    <Route path="/my-requests/match-join/:id" element={<ProtectedRoute><MatchJoinRequestDetail /></ProtectedRoute>} />
                    <Route path="/my-requests/class/:id" element={<ProtectedRoute><ClassEnrollmentDetail /></ProtectedRoute>} />
                    <Route path="/my-requests/spectator/:id" element={<ProtectedRoute><SpectatorRequestDetail /></ProtectedRoute>} />
                    <Route path="/courts/my-bookings" element={<ProtectedRoute><MyRequests /></ProtectedRoute>} />
                    <Route path="/profile/training-schedule" element={<ProtectedRoute><MyTrainingSchedule /></ProtectedRoute>} />
                    <Route path="/courts/booking-management" element={<ProtectedRoute><CourtBookingManagement /></ProtectedRoute>} />
                    <Route path="/courts/booking-history" element={<ProtectedRoute><CourtBookingHistory /></ProtectedRoute>} />
                    <Route path="/courts/schedule-management" element={<ProtectedRoute><CourtScheduleManagement /></ProtectedRoute>} />
                    <Route path="/courts/:id/services" element={<ProtectedRoute><CourtServices /></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                    <Route path="/settings/privacy" element={<ProtectedRoute><PrivacySettings /></ProtectedRoute>} />
                    <Route path="/my-classes" element={<ProtectedRoute><MyClasses /></ProtectedRoute>} />
                    <Route path="/learning-history" element={<ProtectedRoute><LearningHistory /></ProtectedRoute>} />
                    <Route path="/profile/teams" element={<ProtectedRoute><MyTeams /></ProtectedRoute>} />
                    <Route path="/profile/team-invitations" element={<ProtectedRoute><TeamInvitations /></ProtectedRoute>} />
                    <Route path="/profile/team-match-invitations" element={<ProtectedRoute><TeamMatchInvitations /></ProtectedRoute>} />
                    <Route path="/profile/club-invitations" element={<ProtectedRoute><ClubInvitations /></ProtectedRoute>} />
                    <Route path="/profile/invitations" element={<ProtectedRoute><InvitationManagement /></ProtectedRoute>} />
                    <Route path="/teams/:teamId" element={<ProtectedRoute><TeamDetail /></ProtectedRoute>} />

                    {/* Admin routes */}
                    <Route path="/admin" element={<AdminRoute requiredPermission="dashboard"><AdminDashboard /></AdminRoute>} />
                    <Route path="/admin/disputes" element={<AdminRoute requiredPermission="disputes"><AdminDisputes /></AdminRoute>} />
                    <Route path="/admin/court-complaints" element={<AdminRoute requiredPermission="court_complaints"><AdminCourtComplaints /></AdminRoute>} />
                    <Route path="/admin/banned-courts" element={<AdminRoute requiredPermission="banned_courts"><AdminBannedCourts /></AdminRoute>} />
                    <Route path="/admin/court-verification" element={<AdminRoute requiredPermission="court_verification"><AdminCourtVerification /></AdminRoute>} />
                    <Route path="/admin/payments" element={<AdminRoute requiredPermission="payments"><AdminPayments /></AdminRoute>} />
                    <Route path="/admin/player-bans" element={<AdminRoute requiredPermission="player_bans"><AdminPlayerBans /></AdminRoute>} />
                    <Route path="/admin/escrow" element={<AdminRoute requiredPermission="escrow"><AdminEscrow /></AdminRoute>} />
                    <Route path="/admin/payment-reconcile" element={<AdminRoute requiredPermission="escrow"><AdminPaymentReconcile /></AdminRoute>} />
                    <Route path="/admin/payouts" element={<AdminRoute requiredPermission="payouts"><AdminPayouts /></AdminRoute>} />
                    <Route path="/admin/coach-verification" element={<AdminRoute requiredPermission="coach_verification"><AdminCoachVerification /></AdminRoute>} />
                    <Route path="/admin/user-management" element={<AdminRoute requiredPermission="user_management"><AdminUserManagement /></AdminRoute>} />
                    <Route path="/admin/audit-log" element={<AdminRoute requiredPermission="audit_logs"><AdminAuditLog /></AdminRoute>} />
                    <Route path="/admin/cron-logs" element={<AdminRoute requiredPermission="audit_logs"><AdminCronLogs /></AdminRoute>} />
                    <Route path="/admin/reports" element={<AdminRoute requiredPermission="reports"><AdminReports /></AdminRoute>} />
                    <Route path="/admin/fee-config" element={<AdminRoute requiredPermission="fee_config"><AdminFeeConfig /></AdminRoute>} />
                    <Route path="/admin/ai-tester" element={<AdminRoute requiredPermission="ai_tester"><AdminAITester /></AdminRoute>} />
                    <Route path="/admin/ai-tester/history" element={<AdminRoute requiredPermission="ai_tester"><AdminAITesterHistory /></AdminRoute>} />

                    {/* Catch-all */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </OnlinePresenceProvider>
            </RealtimeNotificationsProvider>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
